import React, { Children, cloneElement, isValidElement, ReactNode } from "react";
import defaultReadings from "../../consts/lessonReadings.json";
import styles from "./lessonText.module.scss";
import { useSavedWordPopover } from "../SavedWords/SavedWordsProvider";

const words = Object.keys(defaultReadings).sort((a, b) => b.length - a.length);
const escapeWord = (word: string) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const wordPattern = new RegExp(words.map(escapeWord).join("|"), "gu");
const japanese = /[\p{Script=Han}々\p{Script=Hiragana}\p{Script=Katakana}ー、。！？\s]+(?:[（(]\s*[\p{Script=Hiragana}\p{Script=Katakana}ー、。！？\s]+(?=\s*[,·・）)])|[,]\s+[\p{Script=Hiragana}ー]+(?=\s*[,）)]))/gu;
const naturalReading = /[\p{Script=Han}々\p{Script=Hiragana}\p{Script=Katakana}ー]+\s+(?:is(?:\s+read(?:\s+as)?)?|reads|is pronounced|é|lê-se|se lê|lê-se como|lê-se como sendo)\s+[\p{Script=Hiragana}ー]+/gu;
const excluded = new Set(["ruby", "rt", "rp", "input", "textarea", "svg"]);
function normalizeProvidedReadings(text: string): string {
    // Normalize legacy katakana/mixed-kana readings in the display only; keep
    // authored source strings intact because paragraph notes use their anchors.
    return text.replace(/([\p{Script=Han}々\p{Script=Hiragana}\p{Script=Katakana}ー]+\s*[（(,]\s*)([\p{Script=Hiragana}\p{Script=Katakana}ー]+)/gu, (match, prefix, reading, index) => {
        const hiragana = reading.replace(/[ァ-ヶ]/g, (character: string) => String.fromCharCode(character.charCodeAt(0) - 0x60));
        const word = prefix.replace(/[（(,]\s*$/, "").trim();
        const before = text.slice(0, index).trimEnd();
        if (prefix.includes(",") && !/[（(]$/.test(before) && (defaultReadings as Record<string, string>)[word] !== hiragana) return match;
        return /[\p{Script=Han}]/u.test(prefix) ? prefix + hiragana : match;
    });
}

function providedRanges(text: string): number[][] {
    return [...text.matchAll(japanese), ...text.matchAll(naturalReading)].filter(match => {
        if (!match[0].includes(",")) return true;
        const [word, reading] = match[0].split(",").map(part => part.trim());
        return /[（(]$/.test(text.slice(0, match.index).trimEnd()) || (defaultReadings as Record<string, string>)[word] === reading;
    }).map(match => [match.index!, match.index! + match[0].length]);
}

function withoutProvidedReadings(text: string): string {
    for (const [start, end] of providedRanges(text).sort((a, b) => b[0] - a[0])) text = text.slice(0, start) + " ".repeat(end - start) + text.slice(end);
    return text;
}

function plainText(node: ReactNode): string {
    if (typeof node === "string" || typeof node === "number") return normalizeProvidedReadings(String(node));
    if (isValidElement<{ children?: ReactNode }>(node)) return plainText(node.props.children);
    return Children.toArray(node).map(plainText).join("");
}

/** Explicit readings for new or context-dependent words; never infer a reading from a kanji. */
export function LessonRuby({ children, reading }: { children: ReactNode; reading: string }) {
    if (!/^[\p{Script=Hiragana}ー\s]+$/u.test(reading)) throw new Error("Lesson readings must use hiragana");
    const open = useSavedWordPopover();
    const text = plainText(children);
    const ruby = <ruby className={styles.word} lang="ja">{children}<rp>（</rp><rt>{reading}</rt><rp>）</rp></ruby>;
    return open ? <button type="button" className={styles.wordButton} onClick={(event) => open(text, reading, event.currentTarget)}>{ruby}</button> : ruby;
}

/** Keeps a term interactive when its reading is already written beside it in lesson prose. */
export function LessonSaveTarget({ children, reading, baseWord, baseReading }: { children: ReactNode; reading: string; baseWord?: string; baseReading?: string }) {
    const open = useSavedWordPopover();
    const text = plainText(children);
    return open ? <button type="button" className={styles.wordButton} onClick={(event) => {
        if (baseWord) open(text, reading, event.currentTarget, { baseWord, baseReading });
        else open(text, reading, event.currentTarget);
    }}>{children}</button> : <>{children}</>;
}

/** Annotates lesson prose while keeping links/emphasis and existing readings intact. */
export default function LessonText({ children, readingOverrides }: { children: ReactNode; readingOverrides?: Record<string, string> }) {
    const scopedWords = readingOverrides ? Array.from(new Set([...words, ...Object.keys(readingOverrides)])).sort((a, b) => b.length - a.length) : words;
    const scopedPattern = readingOverrides ? new RegExp(scopedWords.map(escapeWord).join("|"), "gu") : wordPattern;
    const text = normalizeProvidedReadings(plainText(children));
    // Include inline elements when checking existing parenthetical readings.
    // A whole phrase may already have one reading, including its particles.
    const protectedRanges = providedRanges(text);
    let offset = 0;
    function annotate(node: ReactNode): ReactNode {
        if (typeof node === "number") { offset += String(node).length; return node; }
        if (typeof node === "string") {
            const display = text.slice(offset, offset + node.length);
            const start = offset; offset += display.length;
            const result: ReactNode[] = []; let cursor = 0;
            for (const match of display.matchAll(scopedPattern)) {
                const index = match.index!;
                // Never assemble an unknown compound from individual kanji readings.
                if (/[\p{Script=Han}々]/u.test(display[index - 1] || "") || (/[\p{Script=Han}々]$/u.test(match[0]) && /[\p{Script=Han}々]/u.test(display[index + match[0].length] || ""))) continue;
                const reading = readingOverrides?.[match[0]] ?? (defaultReadings as Record<string, string>)[match[0]];
                if (protectedRanges.some(([from, to]) => start + index >= from && start + index < to)) {
                    result.push(display.slice(cursor, index), <LessonSaveTarget key={start + index} reading={reading}>{match[0]}</LessonSaveTarget>);
                } else {
                    result.push(display.slice(cursor, index), <LessonRuby key={start + index} reading={reading}>{match[0]}</LessonRuby>);
                }
                cursor = index + match[0].length;
            }
            return cursor ? [...result, display.slice(cursor)] : display;
        }
        if (isValidElement<{ children?: ReactNode }>(node)) {
            if (node.type === LessonText || node.type === LessonRuby || (typeof node.type === "string" && excluded.has(node.type))) {
                offset += plainText(node).length; return node;
            }
            return node.props.children === undefined ? node : cloneElement(node, {}, Children.map(node.props.children, annotate));
        }
        return Children.map(node, annotate);
    }
    return <>{Children.map(children, annotate)}</>;
}

export function unannotatedLessonKanji(text: string): string[] {
    // The standalone iteration mark 々 has no pronunciation of its own.
    return Array.from(withoutProvidedReadings(text).matchAll(/[\p{Script=Han}]+(?:々[\p{Script=Han}]*)*/gu), match => match[0]).filter(word => word !== "々");
}

/** Used by content coverage checks to identify authored words that still need readings. */
export function missingLessonReadings(text: string): string[] {
    return Array.from(withoutProvidedReadings(text).replace(wordPattern, "").matchAll(/[\p{Script=Han}々]+/gu), match => match[0]);
}
